import React from 'react';
import {
    MoneyHand24Regular,
    QuestionCircle16Regular,
    Location16Regular,
    Video16Regular,
    Tag16Regular,
    ReOrderDotsVertical20Regular,
    Warning16Regular,
    Branch16Regular,
} from "@fluentui/react-icons";
import { ContractStage } from "../../../../models/Entities/Contracts/ContractStage";
import { ConfirmationButtonComponent } from "../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent";
import { ErrorType } from "../../../../models/HttpError";
import { useTranslation } from 'react-i18next';
import StageItemLinkComponent from '../StageItems/StageItemLink/StageItemLink.Component';
import './StageList.Styles.css';

export interface DragPreviewInfo {
    // detached copy of the card, rendered by the list as the thing that follows
    // the cursor
    node: HTMLElement | null;
    width: number;
    offsetX: number;
    offsetY: number;
    x: number;
    y: number;
}

/*
 * The browser always composites its own drag image with transparency, whatever
 * element is handed to setDragImage. So the native one is replaced by a 1x1
 * transparent pixel and the list paints a fully opaque preview that follows the
 * cursor instead.
 */
let emptyDragImage: HTMLImageElement | undefined;
const getEmptyDragImage = (): HTMLImageElement => {
    if (!emptyDragImage) {
        emptyDragImage = new Image();
        emptyDragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    }
    return emptyDragImage;
};

export interface IProps {
    Stage: ContractStage;
    ProtocolId: number | undefined;
    index: number;
    displayOrder?: number;
    isDeleting: boolean;
    failOnDeleting: boolean;
    // the branch this visit pointed at was deleted from the contract
    isOrphanBranch?: boolean;
    orphanBranchId?: number | null;
    // name of the branch the visit is assigned to, when it is still alive
    branchName?: string;
    error: ErrorType | undefined;
    onEdit?: (stage: ContractStage) => void;
    onDelete?: (stage: ContractStage) => void;
    // clears the dangling branch of the visit so it can be edited again
    onDetachBranch?: (stage: ContractStage) => void;
    // drag & drop
    isDragging?: boolean;
    isDragOver?: boolean;
    // side of this card the dragged visit would land on
    dropPosition?: 'before' | 'after' | null;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>, preview: DragPreviewInfo) => void;
    onDragOverCard?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
}


export const StageItemComponent = (props: IProps) => {
    const [t] = useTranslation();
    const s = props.Stage;
    const cardRef = React.useRef<HTMLDivElement | null>(null);

    const cardClassName = [
        'si-card',
        props.isDragging  ? 'si-card--dragging'  : '',
        props.isDragOver  ? 'si-card--drag-over'  : '',
        props.dropPosition === 'before' ? 'si-card--drop-before' : '',
        props.dropPosition === 'after' ? 'si-card--drop-after' : '',
        props.isOrphanBranch ? 'si-card--orphan-branch' : '',
    ].filter(Boolean).join(' ');

    const orphanBranchTooltip = t('stageItem.props.orphanBranchTooltip', {
        branchId: props.orphanBranchId ?? s.branchId,
        defaultValue: 'La rama asociada (#{{branchId}}) fue eliminada del contrato'
    });

    const detachBranchTooltip = t('stageItem.props.detachBranchTooltip', {
        branchId: props.orphanBranchId ?? s.branchId,
        defaultValue: 'Quita la rama #{{branchId}} de la visita para poder volver a editarla'
    });

    // a visit either points at a live branch or at one that no longer exists, never both
    const hasAssignedBranch = !props.isOrphanBranch && s.branchId !== undefined && s.branchId !== null;
    const branchLabel = props.branchName ?? t('stageItem.props.branchFallback', {
        branchId: s.branchId,
        defaultValue: 'Rama #{{branchId}}'
    });

    const assignedBranchTooltip = t('stageItem.props.assignedBranchTooltip', {
        branchName: branchLabel,
        defaultValue: 'Rama asignada a la visita: {{branchName}}'
    });

    const detachAssignedBranchTooltip = t('stageItem.props.detachAssignedBranchTooltip', {
        branchName: branchLabel,
        defaultValue: 'La visita dejará de pertenecer a {{branchName}} y pasará a Sin rama'
    });

    const hasBadges = !!(s.markAsPayable || s.isOptional || props.isOrphanBranch || hasAssignedBranch);

    /*
     * Same detach call behind both branch badges, only the wording of the confirmation
     * changes: recovering a visit stuck on a deleted branch, or plainly unassigning it.
     */
    const renderDetachBranchAction = (title: string, information: string) => props.onDetachBranch && (
        <span
            className="si-badge-action"
            data-stop-stage-edit="true"
            onClick={(e) => e.stopPropagation()}
        >
            <ConfirmationButtonComponent
                ButtonId={"detachbranch" + s.id}
                ButtonIconName={"RemoveLink"}
                ButtonText={""}
                ButtonCssClass={"si-badge-action-button"}
                ConfirmationButtonText={t('stageItem.confirmationButton.detachBranchButton', 'Desvincular')}
                ConfirmationButtonCssClass={""}
                CancelButtonText={t('stageItem.confirmationButton.cancelButton')}
                CancelButtonCssClass={""}
                Title={title}
                Description={""}
                ElementTextHeader={t('stageItem.confirmationButton.detachBranchInformation', 'Visita: ') + s.stageName}
                ElementTextInformation={information}
                onConfirmation={() => props.onDetachBranch?.(s)}
                onCancel={() => {}}
                isOperationExecuting={s.isDetachingBranch === true}
                isOperationExecutedWithError={s.failOnDetachingBranch === true}
                OperationError={s.errorOnDetachingBranch}
            />
        </span>
    );

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        const card = cardRef.current;
        const rect = card?.getBoundingClientRect();

        event.dataTransfer.effectAllowed = 'move';
        try {
            event.dataTransfer.setData('text/plain', String(s.id));
        } catch {
            /* some browsers reject setData on a synthetic event; the drag still works */
        }

        if (typeof event.dataTransfer.setDragImage === 'function') {
            try {
                event.dataTransfer.setDragImage(getEmptyDragImage(), 0, 0);
            } catch {
                /* without setDragImage the browser keeps its own faded snapshot */
            }
        }

        let previewNode: HTMLElement | null = null;
        if (card) {
            const clone = card.cloneNode(true) as HTMLElement;
            clone.classList.remove('si-card--dragging', 'si-card--drag-over', 'si-card--drop-before', 'si-card--drop-after');
            clone.removeAttribute('draggable');
            clone.removeAttribute('id');
            clone.setAttribute('aria-hidden', 'true');
            previewNode = clone;
        }

        props.onDragStart?.(event, {
            node: previewNode,
            width: rect?.width ?? 0,
            offsetX: rect ? event.clientX - rect.left : 0,
            offsetY: rect ? event.clientY - rect.top : 0,
            x: event.clientX,
            y: event.clientY
        });
    };

    return (
        <div
            ref={cardRef}
            className={cardClassName}
            role="button"
            tabIndex={0}
            draggable
            aria-label={`${t('stageItem.props.visit')} ${props.displayOrder ?? s.stageOrder ?? props.index + 1}: ${s.stageName}`}
            onClick={() => props.onEdit?.(s)}
            onKeyDown={(e) => e.key === 'Enter' && props.onEdit?.(s)}
            onDragStart={handleDragStart}
            onDragOver={props.onDragOverCard}
            onDrop={props.onDrop}
            onDragEnd={props.onDragEnd}
        >

            {/* ── DRAG HANDLE ── */}
            <div
                className="si-drag-handle"
                onClick={(e) => e.stopPropagation()}
                title={t('stageItem.props.dragToReorder', 'Arrastrar para reordenar')}
            >
                <ReOrderDotsVertical20Regular />
            </div>

            {/* ── ORDER ── */}
            <div className="si-order">
                <span className="si-order-num">{props.displayOrder ?? s.stageOrder ?? props.index + 1}</span>
                <span className="si-order-label">{t('stageItem.props.visit')}</span>
            </div>

            {/* ── BODY ── */}
            <div className="si-body">

                {/* Row 1: name + cycle */}
                <div className="si-title-row">
                    <span className="si-name" title={s.stageName}>{s.stageName}</span>
                    {s.cycle && (
                        <span className="si-cycle">{s.cycle.cycleName}</span>
                    )}
                </div>

                {/* Row 2: comments */}
                {s.comments && (
                    <p className="si-desc" title={s.comments}>{s.comments}</p>
                )}

                {/* Row 3: execution mode, stage type and badges, all in one line */}
                <div className="si-meta-row">
                    {s.isOnSiteMandatory ? (
                        <span className="si-meta-pill si-meta-pill--inperson"><Location16Regular />{t('stageItem.props.inPerson')}</span>
                    ) : (
                        <span className="si-meta-pill si-meta-pill--remote"><Video16Regular />{t('stageItem.props.remote')}</span>
                    )}
                    {s.contractStageType && (
                        <>
                            <span className="si-meta-divider">·</span>
                            <span className="si-meta-pill si-meta-pill--type"><Tag16Regular />{s.contractStageType.name}</span>
                        </>
                    )}
                    {hasBadges && <span className="si-meta-separator" aria-hidden="true" />}
                    {hasAssignedBranch && (
                        <span className="si-badge si-badge--branch" title={assignedBranchTooltip}>
                            <Branch16Regular /><span className="si-badge-label">{branchLabel}</span>
                            {renderDetachBranchAction(
                                t('stageItem.confirmationButton.detachAssignedBranchTitle', '¿Desea desvincular la rama de la visita?'),
                                detachAssignedBranchTooltip
                            )}
                        </span>
                    )}
                    {props.isOrphanBranch && (
                        <span className="si-badge si-badge--orphan-branch" title={orphanBranchTooltip}>
                            <Warning16Regular />{t('stageItem.props.orphanBranch', 'Rama eliminada')}
                            {renderDetachBranchAction(
                                t('stageItem.confirmationButton.detachBranchTitle', '¿Desea desvincular la rama eliminada?'),
                                detachBranchTooltip
                            )}
                        </span>
                    )}
                    {s.markAsPayable && (
                        <span className="si-badge si-badge--payable">
                            <MoneyHand24Regular />{t('stageItem.props.payable', 'Liquidable')}
                        </span>
                    )}
                    {s.isOptional && (
                        <span className="si-badge si-badge--optional">
                            <QuestionCircle16Regular />{t('stageItem.props.optional')}
                        </span>
                    )}
                </div>

            </div>

            {/* ── ASIDE: link + delete ── */}
            <div
                className="si-aside"
                data-stop-stage-edit="true"
                onClick={(e) => e.stopPropagation()}
            >
                <StageItemLinkComponent
                    ContractStage={s}
                    ProtocolId={props.ProtocolId}
                />
                <ConfirmationButtonComponent
                    ButtonId={"deletestage" + s.id}
                    ButtonIconName={"Delete"}
                    ButtonText={""}
                    ButtonCssClass={""}
                    ConfirmationButtonText={t('stageItem.confirmationButton.deleteButton')}
                    ConfirmationButtonCssClass={""}
                    CancelButtonText={t('stageItem.confirmationButton.cancelButton')}
                    CancelButtonCssClass={""}
                    Title={t('stageItem.confirmationButton.deleteVisit')}
                    Description={""}
                    ElementTextHeader={t('stageItem.confirmationButton.deleteVisitInformation') + s.stageName}
                    ElementTextInformation={t('stageItem.confirmationButton.operationMessage')}
                    onConfirmation={() => props.onDelete?.(s)}
                    onCancel={() => {}}
                    isOperationExecuting={props.isDeleting}
                    isOperationExecutedWithError={props.failOnDeleting}
                    OperationError={props.error}
                />
            </div>

        </div>
    );
};
